import React, { useState, useRef, useEffect, useCallback } from "react";
import {
	Popover,
	MenuItem,
	ListItemIcon,
	InputAdornment,
	OutlinedInput,
	Box,
	CircularProgress,
	Chip,
	Tooltip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import type { SelectOption } from "@components/common/Select/types/types";
import { SelectFieldWrapper } from "@components/common/Select/shared/SelectFieldWrapper";
import {
	SELECT_DROPDOWN_LIST_MAX_HEIGHT,
	checkIconSx,
	menuItemContentSx,
	menuItemLabelSx,
	menuItemSx,
	menuPaperSx,
	menuZIndexSx,
	listItemIconSx,
} from "@components/common/Select/styles/Select.styles";
import FormFieldLabel from "@components/common/LabeledField/FormFieldLabel";
import {
	formFieldPlaceholderColor,
	formFieldSelectedColor,
	outlinedInputExternalLabelProps,
	selectOutlinedInputSx,
} from "@config/theme/formControls";
import { useTranslation } from "react-i18next";
import { truncateEquipmentName } from "../../../EquipmentsTab/utils/equipmentUtils";

interface EquipmentDropdownProps {
	options: SelectOption[];
	selected: string;
	setSelected: (value: string) => void;
	label?: string;
	required?: boolean;
	placeholder?: string;
	id?: string;
	disabled?: boolean;
	onScrollEnd?: () => void;
	isLoading?: boolean;
	isLoadingMore?: boolean;
	hasMoreOptions?: boolean;
	showChip?: boolean;
	onRemoveChip?: () => void;
	onSearchChange?: (value: string) => void;
}

const EquipmentDropdown: React.FC<EquipmentDropdownProps> = ({
	options,
	selected,
	setSelected,
	label,
	required,
	placeholder,
	id,
	disabled,
	onScrollEnd,
	isLoading,
	isLoadingMore = false,
	showChip = false,
	onRemoveChip,
	onSearchChange,
	hasMoreOptions = false,
}) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const anchorRef = useRef<HTMLDivElement>(null);
	const popoverRef = useRef<HTMLDivElement>(null);
	const scrollLoadLockRef = useRef(false);

	const resetSearch = () => {
		setSearch("");
		onSearchChange?.("");
	};

	const handleToggle = () => {
		if (!disabled) {
			if (open) {
				resetSearch();
			}
			setOpen(!open);
		}
	};

	const handleClose = (_event: unknown, reason: "backdropClick" | "escapeKeyDown") => {
		// Ensure focus is properly managed when closing
		if (reason === "escapeKeyDown" || reason === "backdropClick") {
			// Return focus to the anchor element when closing via escape or backdrop
			setTimeout(() => {
				anchorRef.current?.focus();
			}, 0);
		}
		resetSearch();
		setOpen(false);
	};

	const handleOptionClick = (value: string | number) => {
		setSelected(String(value));
		// Clear dropdown search UI only — keep parent search query so the selected item stays in loaded results
		setSearch("");
		// Return focus to the anchor element after selection
		setTimeout(() => {
			anchorRef.current?.focus();
		}, 0);
		setOpen(false);
	};

	useEffect(() => {
		if (!isLoadingMore && !isLoading) {
			scrollLoadLockRef.current = false;
		}
	}, [isLoadingMore, isLoading]);

	const handleScroll = useCallback(
		(e: React.UIEvent<HTMLElement>) => {
			if (!hasMoreOptions || isLoadingMore || isLoading || scrollLoadLockRef.current) {
				return;
			}

			const target = e.currentTarget;
			const { scrollTop, clientHeight, scrollHeight } = target;

			// Avoid treating a non-scrollable list as "at bottom" (prevents fetching every page at once)
			if (scrollHeight <= clientHeight + 1) {
				return;
			}

			if (scrollTop + clientHeight >= scrollHeight - 24) {
				scrollLoadLockRef.current = true;
				onScrollEnd?.();
			}
		},
		[hasMoreOptions, isLoading, isLoadingMore, onScrollEnd],
	);

	const selectedOption = options.find(opt => opt.value === selected);
	const normalizedSearch = search.trim().toLowerCase();
	const filteredOptions =
		onSearchChange || !normalizedSearch
			? options
			: options.filter(option => option.label.toLowerCase().includes(normalizedSearch));

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			// Don't close if clicking inside the popover
			if (popoverRef.current && popoverRef.current.contains(event.target as Node)) {
				return;
			}
			// Don't close if clicking on the anchor
			if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
				return;
			}
			// Close only if clicking outside both
			resetSearch();
			setOpen(false);
		};

		if (open) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [open]);

	// If showing chip, render chip instead of input
	if (showChip && selectedOption) {
		return (
			<>
				{label && (
					<FormFieldLabel htmlFor={id} required={required}>
						{label}
					</FormFieldLabel>
				)}
				<Box className="flex items-center gap-2">
					<Tooltip title={selectedOption.label} arrow placement="top">
						<Chip
							label={truncateEquipmentName(selectedOption.label, 25)}
							onDelete={onRemoveChip}
							deleteIcon={<CloseIcon />}
							sx={{
								backgroundColor: "#e3f2fd",
								color: "#1976d2",
								border: "1px solid #1976d2",
								"& .MuiChip-deleteIcon": {
									color: "#1976d2",
									"&:hover": {
										color: "#1565c0",
									},
								},
							}}
						/>
					</Tooltip>
				</Box>
			</>
		);
	}

	return (
		<>
			{label && (
				<FormFieldLabel htmlFor={id} required={required}>
					{label}
				</FormFieldLabel>
			)}

			<SelectFieldWrapper>
				<Box ref={anchorRef} className="w-full">
					<OutlinedInput
						{...outlinedInputExternalLabelProps}
						value={selectedOption?.label ?? ""}
						placeholder={placeholder || "Please select an equipment"}
						onClick={handleToggle}
						onKeyDown={e => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								handleToggle();
							}
						}}
						readOnly
						fullWidth
						disabled={disabled}
						tabIndex={disabled ? -1 : 0}
						role="combobox"
						aria-expanded={open}
						aria-haspopup="listbox"
						aria-label={label || "Equipment selection"}
						id={id}
						sx={{
							...selectOutlinedInputSx(),
							"& .MuiOutlinedInput-input": {
								fontFamily: "Poppins, sans-serif",
								fontSize: "14px",
								fontWeight: 400,
								cursor: disabled ? "not-allowed" : "pointer",
								color: selectedOption ? formFieldSelectedColor : formFieldPlaceholderColor,
								WebkitTextFillColor: selectedOption ? formFieldSelectedColor : formFieldPlaceholderColor,
							},
						}}
						endAdornment={undefined}
					/>
				</Box>
			</SelectFieldWrapper>

			<Popover
				open={open}
				anchorEl={anchorRef.current}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
				role="listbox"
				aria-label="Equipment options"
				sx={menuZIndexSx}
				PaperProps={{
					ref: popoverRef,
					sx: {
						...menuPaperSx,
						mt: 0.25,
						maxHeight: SELECT_DROPDOWN_LIST_MAX_HEIGHT,
						overflow: "auto",
						minWidth: Math.max(anchorRef.current?.offsetWidth || 300, 400), // Ensure minimum width for long names
						maxWidth: "600px", // Prevent excessive width
						// Custom scrollbar styling
						"&::-webkit-scrollbar": {
							width: "6px",
						},
						"&::-webkit-scrollbar-track": {
							background: "#f1f1f1",
							borderRadius: "3px",
						},
						"&::-webkit-scrollbar-thumb": {
							background: "#c1c1c1",
							borderRadius: "3px",
							"&:hover": {
								background: "#a8a8a8",
							},
						},
					},
					onScroll: handleScroll,
				}}>
				<Box sx={{ p: 1.5, borderBottom: "1px solid #f1f1f1" }}>
					<OutlinedInput
						size="small"
						value={search}
						onChange={e => {
							const value = e.target.value;
							setSearch(value);
							onSearchChange?.(value);
						}}
						placeholder={t("feature.courseBuilder.labs.createLab.searchEquipment")}
						startAdornment={
							<InputAdornment position="start">
								<SearchIcon sx={{ fontSize: 18, color: "#98A2B3" }} />
							</InputAdornment>
						}
						onClick={e => e.stopPropagation()}
						sx={{
							width: "100%",
							"& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
							"& .MuiOutlinedInput-input": { fontSize: "12px", fontFamily: "Poppins, sans-serif" },
						}}
					/>
				</Box>

				{isLoading ? (
					<MenuItem disabled>
						<div className="flex items-center justify-center gap-2 text-gray-500 text-sm py-3 px-4 w-full">
							<CircularProgress size={18} />
							{t("feature.courseBuilder.labs.createLab.loadingMore")}
						</div>
					</MenuItem>
				) : filteredOptions.length === 0 ? (
					<MenuItem disabled>
						<div className="text-gray-500 text-sm py-2 px-4">
							{normalizedSearch
								? t("feature.courseBuilder.labs.createLab.noEquipmentFound")
								: t("feature.courseBuilder.labs.createLab.noEquipmentAvailable")}
						</div>
					</MenuItem>
				) : (
					filteredOptions.map((option, index) => {
						const isSelected = selected === option.value;
						return (
							<MenuItem
								key={option.value || index}
								onClick={() => handleOptionClick(option.value)}
								selected={isSelected}
								sx={menuItemSx(false)}>
								<div style={menuItemContentSx as React.CSSProperties}>
									<Tooltip title={option.label} arrow placement="top">
										<span style={menuItemLabelSx as React.CSSProperties} className="truncate">
											{truncateEquipmentName(option.label, 50)}
										</span>
									</Tooltip>
									{isSelected && (
										<ListItemIcon sx={listItemIconSx}>
											<CheckIcon fontSize="small" sx={checkIconSx} />
										</ListItemIcon>
									)}
								</div>
							</MenuItem>
						);
					})
				)}
				{isLoadingMore && !isLoading ? (
					<MenuItem disabled>
						<div className="flex items-center justify-center gap-2 text-gray-500 text-sm py-2 px-4 w-full">
							<CircularProgress size={16} />
							{t("feature.courseBuilder.labs.createLab.loadingMore")}
						</div>
					</MenuItem>
				) : null}
			</Popover>
		</>
	);
};

export default EquipmentDropdown;
